import React, { useState } from 'react';
import { ChevronRight, ChevronDown, Check, AlertCircle, Info, Award, BookOpen, Star, TrendingUp, AlertTriangle, Eye, EyeOff, File, BarChart2, ArrowLeft, Clock, MessageSquare, Monitor, Calculator, Sparkles, Phone, Smartphone } from 'lucide-react';
import './TCFResults.css';
import TextWithLineBreaks from '../components/Utils/TextWithLineBreaks';
import Footer from '../components/Footer';
import Menu from '../components/Navbar';
import { QRCodeSVG } from 'qrcode.react';

// Composant principal de la page de résultats
const TCFResultsPage = ({ resultsData, resetModule, remainingSubmissions }) => {

  const [activeTab, setActiveTab] = useState(0);
  const [expandedSections, setExpandedSections] = useState({
    global: false,
    language: false,
    structure: false,
    comprehension: false,
    penalties: false
  });

  // Nouvel état pour afficher/masquer la réponse du candidat
  const [showResponse, setShowResponse] = useState(false);

  // S'assurer que nous avons des données valides
  // Section d'erreur améliorée pour TCFResultsPage
  if (!resultsData || !resultsData.results) {
    return (
      <>
        <Menu />
        <div style={{ marginTop: '5rem' }}></div>
        <div className="tcf-no-results-container">
          <div className="tcf-no-results-card">
            <div className="tcf-no-results-icon">
              <AlertCircle size={40} />
            </div>
            <h3 className="tcf-no-results-title">Résultats non disponibles</h3>
            <p className="tcf-no-results-message">
              Nous n'avons pas pu récupérer vos résultats. Veuillez réessayer ultérieurement ou contacter le support si le problème persiste.
            </p>
            <button
              onClick={resetModule}
              className="tcf-no-results-button"
            >
              <ArrowLeft size={18} className="tcf-no-results-button-icon" />
              <span>Retour à l'accueil</span>
            </button>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const subjects = resultsData.results;

  // Fonction pour basculer l'état d'une section
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Fonction pour basculer l'affichage de la réponse
  const toggleResponse = () => {
    setShowResponse(prev => !prev);
  };

  // Calculer la moyenne générale
  const calculateAverageScore = () => {
    const sum = subjects.reduce((acc, subject) => {
      return acc + parseFloat(subject.global_evaluation.final_score || 0);
    }, 0);

    return (sum / subjects.length).toFixed(1);
  };

  const averageScore = calculateAverageScore();

  // Déterminer le niveau CECRL basé sur la moyenne
  const getCECRLLevel = (score) => {
    score = parseFloat(score);
    if (score >= 18) return "C2";
    if (score >= 14) return "C1";
    if (score >= 10) return "B2";
    if (score >= 6) return "B1";
    if (score >= 2) return "A2";
    return "A1";
  };

  const cecrlLevel = getCECRLLevel(averageScore);

  // Déterminer l'échelle de notation et la couleur correspondante
  const getScoreColor = (score) => {
    score = parseFloat(score);
    if (score >= 16) return "tcf-text-green";
    if (score >= 12) return "tcf-text-blue";
    if (score >= 10) return "tcf-text-teal";
    if (score >= 6) return "tcf-text-yellow";
    return "tcf-text-red";
  };

  const getScoreLabel = (score) => {
    score = parseFloat(score);
    if (score >= 16) return "Excellent";
    if (score >= 12) return "Très bon";
    if (score >= 10) return "Bon";
    if (score >= 6) return "Passable";
    return "Insuffisant";
  };

  const getLevelBadge = (level) => {
    const levelMap = {
      "Bien compris": { color: "tcf-bg-green", icon: <Check size={16} className="mr-1" /> },
      "Compris": { color: "tcf-bg-blue", icon: <Info size={16} className="mr-1" /> },
      "Hors sujet": { color: "tcf-bg-red", icon: <AlertTriangle size={16} className="mr-1" /> }
    };

    const defaultStyle = { color: "tcf-bg-gray", icon: <Info size={16} className="mr-1" /> };
    const style = levelMap[level] || defaultStyle;

    return (
      <span className={`tcf-results-badge ${style.color}`}>
        {style.icon}
        {level}
      </span>
    );
  };

  const formatSubjectType = (type) => {
    switch (type) {
      case 1: return "TÂCHE 1";
      case 2: return "TÂCHE 2";
      case 3: return "TÂCHE 3";
      default: return "Tâche d'écriture";
    }
  };

  // Utiliser la réponse réelle de l'utilisateur si disponible
  const getResponseText = (subjectType) => {

    const subject = resultsData.inputData?.subjects?.find(s => s.subjectType === subjectType)

    if (subject && subject.answer) {
      return subject.answer;
    }

    return "Réponse du candidat non disponible.";

  };

  // Rendu des onglets de navigation
  const renderTabs = () => (
    <div className="tcf-results-tabs">
      {subjects.map((subject, index) => (
        <button
          key={index}
          className={`tcf-results-tab-btn ${activeTab === index ? 'active' : ''}`}
          onClick={() => setActiveTab(index)}
        >
          <span className="tcf-tab-text">{formatSubjectType(subject.subject_type)}</span>
          <span className={`tcf-tab-score ${getScoreColor(subject.global_evaluation.final_score)}`}>
            {subject.global_evaluation.final_score}/20
          </span>
        </button>
      ))}
    </div>
  );

  // Rendu de la moyenne générale et du niveau CECRL
  const renderAverageScore = () => {
    return (
      <div className="tcf-results-average-container">
        <div className="tcf-results-average-content">
          <div className="tcf-average-left">
            <BarChart2 size={28} className="tcf-average-icon" />
            <div className="tcf-average-info">
              <h3>Moyenne Générale</h3>
              <p>
                Basée sur {subjects.length} tâche{subjects.length > 1 ? 's' : ''}
              </p>
            </div>
          </div>
          <div className="tcf-average-right">
            <div className="tcf-average-score-container">
              <div className={`tcf-average-score ${getScoreColor(averageScore)}`}>
                {averageScore}
                <span className="tcf-average-suffix">/20</span>
              </div>
            </div>
            <div className="tcf-cecrl-container">
              <div className="tcf-cecrl-label">Niveau CECRL</div>
              <div className="tcf-cecrl-badge">
                {cecrlLevel}
              </div>
            </div>
          </div>
        </div>
        <div className="tcf-cecrl-scale">
          <div className="tcf-cecrl-scale-item tcf-cecrl-a1">
            <span className="tcf-cecrl-scale-level">A1</span>
            <span className="tcf-cecrl-scale-range">1</span>
          </div>
          <div className="tcf-cecrl-scale-item tcf-cecrl-a2">
            <span className="tcf-cecrl-scale-level">A2</span>
            <span className="tcf-cecrl-scale-range">2 à 5</span>
          </div>
          <div className="tcf-cecrl-scale-item tcf-cecrl-b1">
            <span className="tcf-cecrl-scale-level">B1</span>
            <span className="tcf-cecrl-scale-range">6 à 9</span>
          </div>
          <div className="tcf-cecrl-scale-item tcf-cecrl-b2">
            <span className="tcf-cecrl-scale-level">B2</span>
            <span className="tcf-cecrl-scale-range">10 à 13</span>
          </div>
          <div className="tcf-cecrl-scale-item tcf-cecrl-c1">
            <span className="tcf-cecrl-scale-level">C1</span>
            <span className="tcf-cecrl-scale-range">14 à 17</span>
          </div>
          <div className="tcf-cecrl-scale-item tcf-cecrl-c2">
            <span className="tcf-cecrl-scale-level">C2</span>
            <span className="tcf-cecrl-scale-range">18 à 20</span>
          </div>
        </div>
      </div>
    );
  };

  // Rendu de la réponse du candidat et de la consigne
  const renderTaskAndResponse = (subject) => {
    return (
      <div className="tcf-results-task-container mb-6">
        <div className="tcf-results-task-header" onClick={toggleResponse}>
          <div className="tcf-header-left">
            <File size={22} className="tcf-header-icon" />
            <h3>Consigne et Réponse</h3>
          </div>
          <button className="tcf-toggle-btn">
            {showResponse ? <EyeOff size={20} /> : <Eye size={20} />}
          </button>
        </div>

        {showResponse && (
          <div className="tcf-results-task-content">
            <div className="tcf-results-task-info">
              <h4>Consigne de la tâche :</h4>
              <TextWithLineBreaks text={subject.task_info.instruction} />
            </div>

            <div className="tcf-results-candidate-response">
              <h4>Votre réponse :</h4>
              <pre>
                {getResponseText(subject.subject_type)}
              </pre>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Rendu de l'évaluation globale
  const renderGlobalEvaluation = (subject) => {
    const { global_evaluation } = subject;

    return (
      <div className="tcf-results-global mb-6">
        <div
          className="tcf-results-section-header"
          onClick={() => toggleSection('global')}
        >
          <div className="tcf-header-left">
            <Award size={22} className="tcf-header-icon" />
            <h3>Évaluation Globale</h3>
          </div>
          {expandedSections.global ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
        </div>

        {expandedSections.global && (
          <div className="tcf-results-section-content">
            <div className="tcf-results-score-card">
              <div className="tcf-grid">
                <div className="tcf-results-score-item">
                  <p>Score Final</p>
                  <div className={`score-value ${getScoreColor(global_evaluation.final_score)}`}>
                    {global_evaluation.final_score}<span className="score-suffix">/20</span>
                  </div>
                  <p className={`score-label ${getScoreColor(global_evaluation.final_score)}`}>
                    {getScoreLabel(global_evaluation.final_score)}
                  </p>
                </div>

                <div className="tcf-results-score-item">
                  <p>Nombre de mots</p>
                  <div className="tcf-word-count-container">
                    <div className="tcf-word-count">
                      <span className="tcf-word-actual">{global_evaluation.word_count}</span>
                      <span className="tcf-word-label">Utilisé</span>
                    </div>
                    <div className="tcf-word-divider">
                      <BookOpen size={24} />
                    </div>
                    <div className="tcf-word-required">
                      <span>{global_evaluation.word_count_requirements}</span>
                      <span className="tcf-word-label">Requis</span>
                    </div>
                  </div>
                </div>

                <div className="tcf-results-score-item">
                  <p>Type de document</p>
                  <div className="tcf-doc-type">
                    <span className="tcf-doc-actual">{subject.document_analysis.detected_type}</span>
                    {subject.structure_analysis.type_match && (
                      <Check size={16} className="tcf-doc-check" />
                    )}
                    <span className="tcf-doc-expected">
                      Attendu: {subject.document_analysis.expected_document_type}
                    </span>
                  </div>
                </div>
              </div>

              <div className="tcf-results-strengths-areas">
                <div className="tcf-results-strengths">
                  <h4>
                    <Star size={18} />
                    Points forts
                  </h4>
                  <ul>
                    {global_evaluation.strengths.map((strength, idx) => (
                      <li key={idx}>
                        <Check size={14} />
                        <span>{strength}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="tcf-results-improvements">
                  <h4>
                    <TrendingUp size={18} />
                    Points à améliorer
                  </h4>
                  <ul>
                    {global_evaluation.improvement_areas.map((area, idx) => (
                      <li key={idx}>
                        <AlertCircle size={14} />
                        <span>{area}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Rendu de l'analyse linguistique
  const renderLanguageAnalysis = (subject) => {
    const { language_analysis } = subject;

    // Si pas d'erreurs, afficher un message simplifié
    if (language_analysis.error_count === 0) {
      return (
        <div className="tcf-results-language mb-6">
          <div
            className="tcf-results-section-header"
            onClick={() => toggleSection('language')}
          >
            <div className="tcf-header-left">
              <BookOpen size={22} className="tcf-header-icon" />
              <h3>Analyse Linguistique</h3>
              <span className={`tcf-section-score ${getScoreColor(language_analysis.score)}`}>
                {language_analysis.score}/20
              </span>
            </div>
            {expandedSections.language ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
          </div>

          {expandedSections.language && (
            <div className="tcf-results-section-content">
              <div className="tcf-results-perfect-score">
                <div className="tcf-perfect-icon">
                  <Check size={40} className="text-green-500" />
                </div>
                <div className="tcf-perfect-message">
                  <h4>Excellente maîtrise linguistique</h4>
                  <p>Aucune faute n'a été détectée dans votre texte. Continuez votre excellent travail !</p>
                </div>
              </div>
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="tcf-results-language mb-6">
        <div
          className="tcf-results-section-header"
          onClick={() => toggleSection('language')}
        >
          <div className="tcf-header-left">
            <BookOpen size={22} className="tcf-header-icon" />
            <h3>Analyse Linguistique</h3>
            <span className={`tcf-section-score ${getScoreColor(language_analysis.score)}`}>
              {language_analysis.score}/10
            </span>
          </div>
          {expandedSections.language ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
        </div>

        {expandedSections.language && (
          <div className="tcf-results-section-content">
            <div className="tcf-results-language-overview">
              {language_analysis.errors && language_analysis.errors.length > 0 && (
                <div className="tcf-results-errors-details">
                  <h4>Erreurs à corriger</h4>
                  <div className="tcf-errors-list">
                    {language_analysis.errors.map((error, idx) => (
                      <div key={idx} className="tcf-error-item">
                        <div className="tcf-error-header">
                          <span className="tcf-error-badge">Sévérité: {error.severity}</span>
                        </div>
                        <div className="tcf-error-text">
                          <p>{error.original_text}</p>
                        </div>
                        <div className="tcf-correction">
                          <p>{error.suggested_correction}</p>
                        </div>
                        <p className="tcf-error-explanation">{error.explanation}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Rendu de l'analyse structurelle
  const renderStructureAnalysis = (subject) => {
    const { structure_analysis } = subject;

    return (
      <div className="tcf-results-structure mb-6">
        <div
          className="tcf-results-section-header"
          onClick={() => toggleSection('structure')}
        >
          <div className="tcf-header-left">
            <Info size={22} className="tcf-header-icon" />
            <h3>Analyse Structurelle</h3>
            <span className={`tcf-section-score ${getScoreColor(structure_analysis.structure_score)}`}>
              {structure_analysis.structure_score}/20
            </span>
          </div>
          {expandedSections.structure ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
        </div>

        {expandedSections.structure && (
          <div className="tcf-results-section-content">
            <div className="tcf-results-structure-overview">
              <div className="tcf-structure-header">
                <div className="tcf-structure-type">
                  <span className="tcf-type-label">Type de document:</span>
                  <div className="tcf-type-match">
                    <span>{structure_analysis.detected_type}</span>
                    {structure_analysis.type_match ? (
                      <Check size={16} className="tcf-type-check" />
                    ) : (
                      <AlertCircle size={16} className="tcf-type-error" />
                    )}
                  </div>
                </div>
              </div>

              {/* Afficher uniquement les éléments manquants ou incorrects */}
              {structure_analysis.structure_components && (
                <div className="tcf-missing-components">
                  {structure_analysis.structure_components
                    .filter(component => !component.is_present || !component.is_correct)
                    .map((component, idx) => (
                      <div key={idx} className="tcf-component-item">
                        <div className="tcf-component-header">
                          <span className="tcf-component-name">{component.name}</span>
                          <div className="tcf-component-badges">
                            {!component.is_present ? (
                              <span className="tcf-results-badge tcf-bg-red">
                                <AlertCircle size={12} className="tcf-badge-icon" />
                                Absent
                              </span>
                            ) : !component.is_correct ? (
                              <span className="tcf-results-badge tcf-bg-yellow">
                                <AlertTriangle size={12} className="tcf-badge-icon" />
                                Incorrect
                              </span>
                            ) : null}
                          </div>
                        </div>
                        <p className="tcf-component-explanation">{component.explanation}</p>
                      </div>
                    ))}
                </div>
              )}

              {structure_analysis.suggestions && structure_analysis.suggestions.length > 0 && (
                <div className="tcf-structure-suggestions">
                  <h4>Suggestions d'amélioration</h4>
                  <ul className="tcf-suggestions-list">
                    {structure_analysis.suggestions.map((suggestion, idx) => (
                      <li key={idx} className="tcf-suggestion-item">
                        <Info size={14} className="tcf-suggestion-icon" />
                        <span>{suggestion}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Rendu de l'analyse de compréhension
  const renderComprehensionAnalysis = (subject) => {
    const { comprehension_analysis } = subject;

    return (
      <div className="tcf-results-comprehension mb-6">
        <div
          className="tcf-results-section-header"
          onClick={() => toggleSection('comprehension')}
        >
          <div className="tcf-header-left">
            <BookOpen size={22} className="tcf-header-icon" />
            <h3>Analyse de Compréhension</h3>
            <div className="tcf-comp-badges">
              {getLevelBadge(comprehension_analysis.level)}
              <span className={`tcf-section-score ${getScoreColor(comprehension_analysis.score)}`}>
                {comprehension_analysis.score}/20
              </span>
            </div>
          </div>
          {expandedSections.comprehension ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
        </div>

        {expandedSections.comprehension && (
          <div className="tcf-results-section-content">
            <div className="tcf-results-comprehension-overview">
              <div className="tcf-comp-header">
                <div className="tcf-comp-level-container">
                  <span className="tcf-comp-level-label">Niveau:</span>
                  {getLevelBadge(comprehension_analysis.level)}
                </div>
                <div className="tcf-comp-score-container">
                  <span className="tcf-comp-score-label">Score:</span>
                  <span className={`tcf-comp-score-value ${getScoreColor(comprehension_analysis.score)}`}>
                    {comprehension_analysis.score}/20
                  </span>
                </div>
              </div>

              {/* Afficher uniquement les éléments manquants */}
              {comprehension_analysis.missing_elements && comprehension_analysis.missing_elements.length > 0 && (
                <div className="tcf-missing-elements">
                  <h4>Éléments manquants ou insuffisants</h4>
                  <div className="tcf-elements-grid">
                    {comprehension_analysis.missing_elements.map((element, idx) => (
                      <div key={idx} className="tcf-element-item tcf-missing-item">
                        <div className="tcf-element-header">
                          <div className="tcf-element-title">
                            <AlertCircle size={16} className="tcf-element-icon" />
                            <p>{element.element}</p>
                          </div>
                          <span className={`tcf-element-badge ${element.importance === "Essentiel" ? 'tcf-bg-red' : 'tcf-bg-yellow'}`}>
                            {element.importance}
                          </span>
                        </div>
                        {element.suggestion && (
                          <div className="tcf-element-suggestion">
                            <p>
                              <span>Suggestion:</span> {element.suggestion}
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Nouveau: Rendu des exercices restants
  const renderRemainingSubmissions = () => {
    return (
      <div className="tcf-remaining-submissions">
        <div className="tcf-remaining-icon">
          <Clock size={20} />
        </div>
        <div className="tcf-remaining-text">
          <span>Examens restants : </span>
          <strong>{remainingSubmissions}</strong>
        </div>
      </div>
    );
  };



  // Rendu des pénalités
  const renderPenalties = (subject) => {
    const { penalties, global_evaluation } = subject;
    const wordCountPenalty = penalties?.wordCountPenalty || 0;
    const tonePenalty = penalties?.tonePenalty || 0;
    const totalPenalty = wordCountPenalty + tonePenalty;

    // Si aucune pénalité, on n'affiche pas la section
    if (totalPenalty === 0) {
      return null;
    }

    return (
      <div className="tcf-results-penalties mb-6">
        <div
          className="tcf-results-section-header"
          onClick={() => toggleSection('penalties')}
        >
          <div className="tcf-header-left">
            <AlertTriangle size={22} className="tcf-header-icon" style={{ color: '#e74c3c' }} />
            <h3>Pénalités Appliquées</h3>
            <span className="tcf-section-score" style={{ backgroundColor: '#e74c3c' }}>
              -{totalPenalty} pts
            </span>
          </div>
          {expandedSections.penalties ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
        </div>

        {expandedSections.penalties && (
          <div className="tcf-results-section-content">
            {wordCountPenalty > 0 && (
              <div className="tcf-penalty-item">
                <div className="tcf-penalty-header">
                  <File size={18} style={{ color: '#e74c3c' }} />
                  <span className="tcf-penalty-title">Nombre de mots</span>
                </div>
                <p className="tcf-penalty-description">
                  {global_evaluation.word_count} mots écrits sur {global_evaluation.word_count_requirements} requis
                </p>
                <div className="tcf-penalty-score">
                  <span className="tcf-penalty-score-text">-{wordCountPenalty} points</span>
                </div>
              </div>
            )}

            {tonePenalty > 0 && (
              <div className="tcf-penalty-item">
                <div className="tcf-penalty-header">
                  <MessageSquare size={18} style={{ color: '#e74c3c' }} />
                  <span className="tcf-penalty-title">Ton inapproprié</span>
                </div>
                <p className="tcf-penalty-description">
                  Le ton utilisé n'est pas adapté au contexte de la tâche
                </p>
                <div className="tcf-penalty-score">
                  <span className="tcf-penalty-score-text">-{tonePenalty} points</span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Rendu de la section d'explication du système de notation
  const renderExplanationSection = () => (
    <div className="tcf-explanation-section">
      <div className="tcf-explanation-header">
        <Info size={22} className="tcf-explanation-icon" />
        <h3 className="tcf-explanation-title">Comment fonctionne l'évaluation ?</h3>
      </div>

      <div className="tcf-explanation-content">
        <p className="tcf-explanation-subtitle">Calcul des notes par tâche :</p>

        <div className="tcf-explanation-item">
          <Check size={16} className="tcf-explanation-check" />
          <p className="tcf-explanation-text">
            <span className="tcf-bold">Analyse linguistique (30%)</span> : Grammaire, vocabulaire, orthographe
          </p>
        </div>

        <div className="tcf-explanation-item">
          <Check size={16} className="tcf-explanation-check" />
          <p className="tcf-explanation-text">
            <span className="tcf-bold">Structure (25%)</span> : Organisation du texte, cohérence
          </p>
        </div>

        <div className="tcf-explanation-item">
          <Check size={16} className="tcf-explanation-check" />
          <p className="tcf-explanation-text">
            <span className="tcf-bold">Compréhension (45%)</span> : Réponse à la consigne
          </p>
        </div>

        <div className="tcf-penalties-note">
          <AlertTriangle size={16} className="tcf-penalties-icon" />
          <p className="tcf-penalties-text">
            Pénalités possibles : -9 pts pour le non respect du nombre de mots, -2 pts pour le ton inapproprié
          </p>
        </div>

        <div className="tcf-average-calculation">
          <p className="tcf-explanation-subtitle">Note générale :</p>
          <div className="tcf-explanation-item">
            <Calculator size={16} className="tcf-explanation-check" />
            <p className="tcf-explanation-text">
              Moyenne arithmétique simple des notes de toutes les tâches
            </p>
          </div>
        </div>

        <div className="tcf-ai-notice">
          <Sparkles size={16} className="tcf-ai-icon" />
          <p className="tcf-ai-text">
            Cette correction est réalisée par notre agent IA spécialisé et peut contenir des erreurs de correction.
          </p>
        </div>
      </div>
    </div>
  );


  // Rendu de la section d'informations sur l'application web avec QR code
  const renderWebAppInfo = () => {
    const phoneAppUrl = "https://bit.ly/examstcfcanada";

    return (
      <div className="tcf-webapp-notice">
        <div className="tcf-webapp-content">
          <div className="tcf-webapp-text-container">
            <div className="tcf-webapp-header">
              <Monitor size={18} className="tcf-webapp-icon" />
              <h4 className="tcf-webapp-title">Téléchargez l'application Android</h4>
            </div>
            <p className="tcf-webapp-text">
            Pour une expérience optimale sur mobile Android, nous vous recommandons de télécharger notre 
              <a href={phoneAppUrl} className="tcf-web-link"> application Android</a> avec vos mêmes identifiants.
            </p>
            <span className="tcf-web-url">{phoneAppUrl}</span>
          </div>

          <div className="tcf-qrcode-container">
            <QRCodeSVG
              value={phoneAppUrl}
              size={120}
              bgColor={"#ffffff"}
              fgColor={"#000000"}
              level={"L"}
              includeMargin={false}
              className="tcf-qrcode"
            />
            <p className="tcf-qrcode-caption">Scannez pour Télécharger</p>
          </div>
        </div>
      </div>
    );
  };

  const currentSubject = subjects[activeTab];

  return (
    <>
      <div className="tcf-results-container p-md-1 p-2">
        {/* Background decorative elements */}
        <div
          className="position-absolute"
          style={{
            top: '5%',
            left: '3%',
            width: '15rem',
            height: '15rem',
            background: 'radial-gradient(circle, rgba(77,166,255,0.03) 0%, rgba(71,216,224,0) 70%)',
            borderRadius: '50%',
            zIndex: 0
          }}
        />
        <div
          className="position-absolute"
          style={{
            bottom: '8%',
            right: '5%',
            width: '20rem',
            height: '20rem',
            background: 'radial-gradient(circle, rgba(77,166,255,0.02) 0%, rgba(71,216,224,0) 70%)',
            borderRadius: '50%',
            zIndex: 0
          }}
        />

        <div className="tcf-results-header">
          <div className="tcf-results-title-wrapper">
            <h2>
              Résultats d'Expression Écrite
            </h2>
            {renderRemainingSubmissions()}
          </div>
          <p>
            Analyse et évaluation détaillée de vos réponses au TCF Canada
          </p>
        </div>

        {renderAverageScore()}

        {renderTabs()}

        <div className="tcf-results-content">
          {renderTaskAndResponse(currentSubject)}
          {renderGlobalEvaluation(currentSubject)}
          {renderLanguageAnalysis(currentSubject)}
          {renderStructureAnalysis(currentSubject)}
          {renderComprehensionAnalysis(currentSubject)}
          {renderPenalties(currentSubject)}
        </div>

        {/* Section d'explication et informations sur l'application web */}
        {renderExplanationSection()}
        {renderWebAppInfo()}

        {/* Bouton pour retourner à l'écran de sélection */}
        <div className="tcf-bottom-actions">
          <button
            onClick={resetModule}
            className="tcf-upgrade-button"
          >
            <ArrowLeft size={20} className="tcf-bottom-return-icon" />
            <span>Commencer un nouvel examen</span>
          </button>

          <a
            href="/offres"
            className="tcf-upgrade-button"
          >
            <Star size={18} />
            <span>Voir nos offres d'abonnement</span>
          </a>
        </div>
      </div>
    </>
  );
};

export default TCFResultsPage;